import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RotateCcw, Share2 } from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import sortingHatImg from '../media/sortinghat.png';

const VERDICT_LINES = [
  'I see...',
  'Well then...',
  'Let it be...',
];

function handleShare(school) {
  const text = `The Sorting Hat has spoken — ${school} is my match! Find yours:`;
  const url = window.location.href;
  if (navigator.share) {
    navigator.share({ title: 'The Sorting Hat has spoken!', text, url }).catch(() => {});
  } else {
    navigator.clipboard?.writeText(`${text} ${url}`).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {});
  }
}

export default function SortingHatVerdict({ school, onViewDetails, onRestart, skipAnimation = false }) {
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [speechStep, setSpeechStep] = useState(() => skipAnimation ? VERDICT_LINES.length : 0);
  const [hatMode, setHatMode] = useState(() => skipAnimation ? 'announcing' : 'pondering');
  const [showButton, setShowButton] = useState(() => skipAnimation);
  const [schoolNameStyle, setSchoolNameStyle] = useState({});
  const speechZoneRef = useRef(null);
  const schoolNameRef = useRef(null);

  useEffect(() => {
    if (skipAnimation) {
      setSpeechStep(VERDICT_LINES.length);
      setHatMode('announcing');
      setShowButton(true);
      setSchoolNameStyle({});
      return;
    }

    setSpeechStep(0);
    setHatMode('pondering');
    setShowButton(false);
    setSchoolNameStyle({});

    const timers = [
      setTimeout(() => setSpeechStep(1), 1800),
      setTimeout(() => {
        setSpeechStep(2);
        setHatMode('shaking');
      }, 3400),
      setTimeout(() => {
        setHatMode('announcing');
        setSpeechStep(3);
      }, 5000),
      setTimeout(() => setShowButton(true), 7600),
    ];

    return () => timers.forEach(clearTimeout);
  }, [school, skipAnimation]);

  useLayoutEffect(() => {
    if (speechStep < VERDICT_LINES.length || !schoolNameRef.current || !speechZoneRef.current) return;

    const fitSchoolName = () => {
      const node = schoolNameRef.current;
      const zone = speechZoneRef.current;
      if (!node || !zone) return;

      const maxFont = 92;
      const minFont = 28;
      let chosenFont = minFont; // fallback if nothing fits in 2 lines

      for (let fontSize = maxFont; fontSize >= minFont; fontSize -= 2) {
        const lineHeight = fontSize >= 64 ? 1.05 : fontSize >= 46 ? 1.08 : 1.12;
        node.style.fontSize = `${fontSize}px`;
        node.style.lineHeight = String(lineHeight);

        // Must fit in 2 lines AND within the zone height
        const maxTwoLinesHeight = Math.ceil(2 * fontSize * lineHeight) + 4;
        if (node.scrollHeight <= Math.min(zone.clientHeight, maxTwoLinesHeight)) {
          chosenFont = fontSize;
          break;
        }
      }

      const chosenLineHeight = chosenFont >= 64 ? 1.05 : chosenFont >= 46 ? 1.08 : 1.12;
      setSchoolNameStyle({
        fontSize: `${chosenFont}px`,
        lineHeight: chosenLineHeight,
      });
    };

    fitSchoolName();

    // Re-measure once fonts are guaranteed loaded (fixes first-render race
    // where Cinzel Decorative hasn't arrived yet and fallback metrics are used)
    let rafId;
    document.fonts.ready.then(() => {
      rafId = requestAnimationFrame(fitSchoolName);
    });

    window.addEventListener('resize', fitSchoolName);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', fitSchoolName);
    };
  }, [school, speechStep]);

  return (
    <div className="sh-verdict-scene">
      {showRestartConfirm && (
        <ConfirmModal
          title="Start Anew?"
          message="The Hat's work will be undone — your schools, your scores, and this verdict shall all be forgotten."
          confirmLabel="Yes, start anew"
          cancelLabel="No, stay"
          onConfirm={() => { setShowRestartConfirm(false); onRestart(); }}
          onCancel={() => setShowRestartConfirm(false)}
        />
      )}
      <div className="sh-verdict-hat-stage">
        <img
          src={sortingHatImg}
          alt="Sorting Hat"
          draggable="false"
          className={`sh-verdict-hat sh-verdict-hat--${hatMode}`}
        />
      </div>

      <div ref={speechZoneRef} className="sh-speech sh-verdict-speech" aria-live="polite">
        {speechStep < VERDICT_LINES.length ? (
          <p key={VERDICT_LINES[speechStep]} className="sh-speech-line sh-verdict-line">
            <em>{VERDICT_LINES[speechStep]}</em>
          </p>
        ) : (
          <p
            key={school}
            ref={schoolNameRef}
            className="sh-verdict-school-name"
            style={schoolNameStyle}
          >
            {school}
          </p>
        )}
      </div>

      <div className={`sh-verdict-actions${showButton ? ' is-visible' : ''}`}>
        <div className="sh-verdict-btn-group">
          <button className="sh-reveal-btn sh-verdict-btn" onClick={onViewDetails}>
            View The Hat's Reasoning
          </button>
          <span className="sh-verdict-btn-divider" aria-hidden="true" />
          <button
            className="sh-verdict-icon-btn"
            onClick={() => setShowRestartConfirm(true)}
            title="Start the sorting anew"
            aria-label="Restart"
          >
            <RotateCcw size={16} />
          </button>
          <button
            className="sh-verdict-icon-btn"
            onClick={() => handleShare(school)}
            title="Share your result"
            aria-label="Share"
          >
            <Share2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
